class OurStory extends HTMLElement {
  #isDragging = false;
  #startX = 0;
  #currentX = 0;
  #scrollContainer = null;
  #svgPaths = null;
  #pathLengths = [];
  #endColor = '#008aff';

  constructor() {
    super();
    this.animation = null;
  }

  connectedCallback() {
    if (document.readyState === 'complete') {
      this.init();
    } else {
      window.addEventListener('load', () => this.init());
    }
  }

  getGSAP() {
    if (typeof window !== 'undefined' && window.gsap && typeof window.gsap.to === 'function') {
      return window.gsap;
    }
    if (typeof window !== 'undefined' && window.GSAP && typeof window.GSAP.to === 'function') {
      return window.GSAP;
    }
    if (typeof window !== 'undefined' && window.GreenSockGlobals && window.GreenSockGlobals.gsap) {
      return window.GreenSockGlobals.gsap;
    }
    if (typeof gsap !== 'undefined' && typeof gsap.to === 'function') {
      return gsap;
    }
    return null;
  }

  init() {
    this.#scrollContainer = this.querySelector('.our-story__scroll-container');
    const overlayWrapper = this.querySelector('.our-story__svg-overlay-wrapper');
    this.#svgPaths = this.querySelectorAll('.our-story__svg-path');
    
    if (!this.#scrollContainer || !overlayWrapper) {
      console.warn('Our Story: Missing required elements', {
        scrollContainer: !!this.#scrollContainer,
        overlayWrapper: !!overlayWrapper,
        svgPaths: this.#svgPaths?.length || 0
      });
      return;
    }

    if (!this.#svgPaths || this.#svgPaths.length === 0) {
      console.warn('Our Story: No SVG paths found');
      return;
    }

    this.#pathLengths = [];
    this.#svgPaths.forEach((path, index) => {
      const pathLength = path.getTotalLength();
      this.#pathLengths[index] = pathLength;
      
      path.setAttribute('stroke-dasharray', `${pathLength} ${pathLength * 2}`);
      path.setAttribute('stroke-dashoffset', -pathLength * 0.9);
      path.setAttribute('stroke', this.#endColor);
      path.setAttribute('opacity', '1');
      
      if (!path.getAttribute('stroke-width')) {
        path.setAttribute('stroke-width', '10');
      }
      
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
    });

    this.setupDragToScroll();
    this.setupScrollTracking();
    this.updateSVGFill();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#svgPaths.forEach((path) => {
        path.setAttribute('stroke', this.#endColor);
        path.setAttribute('stroke-dashoffset', 0);
        path.setAttribute('opacity', '1');
      });
      return;
    }

    const gsap = this.getGSAP();
    
    if (gsap) {
      const hasScrollTrigger = typeof ScrollTrigger !== 'undefined' || 
                              (gsap.plugins && gsap.plugins.scrollTrigger);
      
      if (hasScrollTrigger && gsap.registerPlugin) {
        try {
          gsap.registerPlugin(ScrollTrigger || gsap.plugins.scrollTrigger);
        } catch (e) {
          // Registration failed
        }
      }
      
      this.setupGSAPAnimation(this.#scrollContainer, overlayWrapper, gsap);
    }
  }

  setupDragToScroll() {
    if (!this.#scrollContainer) return;
    this.#setupDragHandlers();
  }

  #setupDragHandlers() {
    if (!this.#scrollContainer) return;

    const preventDefault = (e) => e.preventDefault();

    this.#scrollContainer.addEventListener('pointerdown', (event) => {
      if (event.target.closest('a, button, input, select, textarea')) {
        return;
      }

      event.preventDefault();
      this.#isDragging = true;
      this.#startX = event.clientX;
      const startScrollLeft = this.#scrollContainer.scrollLeft;
      
      this.classList.add('our-story--dragging');
      this.#scrollContainer.style.cursor = 'grabbing';

      const controller = new AbortController();
      const { signal } = controller;

      const onPointerMove = (event) => {
        if (!this.#isDragging) return;

        const deltaX = this.#startX - event.clientX;
        this.#scrollContainer.scrollLeft = startScrollLeft + deltaX;
        this.updateSVGFill();
      };

      const onPointerUp = () => {
        controller.abort();
        this.#isDragging = false;
        this.classList.remove('our-story--dragging');
        this.#scrollContainer.style.cursor = 'grab';
      };

      document.addEventListener('pointermove', onPointerMove, { signal });
      document.addEventListener('pointerup', onPointerUp, { signal });
      document.addEventListener('pointercancel', onPointerUp, { signal });
      document.addEventListener('pointercapturelost', onPointerUp, { signal });
      document.addEventListener('click', preventDefault, { once: true, signal });
    });
  }

  setupScrollTracking() {
    if (!this.#scrollContainer) return;

    this.#scrollContainer.addEventListener('scroll', () => {
      this.updateSVGFill();
    }, { passive: true });
  }

  getScrollProgress() {
    if (!this.#scrollContainer) return 0;
    
    const scrollWidth = this.#scrollContainer.scrollWidth;
    const clientWidth = this.#scrollContainer.clientWidth;
    const scrollLeft = this.#scrollContainer.scrollLeft;
    const maxScroll = scrollWidth - clientWidth;
    
    if (maxScroll <= 0) return 0;
    
    const progress = scrollLeft / maxScroll;
    return Math.max(0, Math.min(1, progress));
  }

  updateSVGFill() {
    if (!this.#svgPaths || this.#svgPaths.length === 0) {
      console.warn('Our Story: No SVG paths to update');
      return;
    }
    
    const progress = this.getScrollProgress();
    
    this.#svgPaths.forEach((path, index) => {
      const pathLength = this.#pathLengths[index] || path.getTotalLength();
      const visibleRange = 0.9;
      const dashOffset = -pathLength * (0.9 - (progress * visibleRange));
      
      path.setAttribute('stroke-dasharray', `${pathLength} ${pathLength * 2}`);
      path.setAttribute('stroke-dashoffset', dashOffset);
      path.setAttribute('stroke', this.#endColor);
      path.setAttribute('opacity', '1');
    });
  }

  setupGSAPAnimation(scrollContainer, overlayWrapper, gsap) {
    const hasScrollTrigger = typeof ScrollTrigger !== 'undefined' || 
                            (gsap.plugins && gsap.plugins.scrollTrigger);

    if (hasScrollTrigger) {
      this.animation = gsap.to(this.#svgPaths, {
        stroke: this.#endColor,
        ease: 'none',
        scrollTrigger: {
          trigger: scrollContainer,
          start: 'left left',
          end: 'right left',
          scrub: true,
          horizontal: true,
          invalidateOnRefresh: true,
          anticipatePin: 1,
          onUpdate: (self) => {
            const progress = self.progress;
            this.#svgPaths.forEach((path, index) => {
              const pathLength = this.#pathLengths[index] || path.getTotalLength();
              const visibleRange = 0.9;
              const dashOffset = -pathLength * (0.9 - (progress * visibleRange));
              
              path.setAttribute('stroke-dasharray', `${pathLength} ${pathLength * 2}`);
              path.setAttribute('stroke-dashoffset', dashOffset);
              path.setAttribute('stroke', this.#endColor);
              path.setAttribute('opacity', '1');
            });
          }
        }
      });
    }
  }

  disconnectedCallback() {
    if (this.animation && this.animation.scrollTrigger) {
      this.animation.scrollTrigger.kill();
    }
  }
}

if (!customElements.get('our-story')) {
  customElements.define('our-story', OurStory);
}
